// Plant pricing calculator in the shared admin page frame.
// Updated: MargesTab now stores margins as percentages of (purchase + logistics per plant) instead of cent amounts.
// Features: Variant configuration (purchase price, plants/box), margin tier CRUD per product or per variant, live pricing calculator

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/lib/supabase';
import { usePlantPriceCalculation, formatEuroCents } from '@/lib/plant-pricing';
import Breadcrumb from '@/components/admin/Breadcrumb';
import { Calculator, Package, Layers, Plus, Trash2, Pencil, TriangleAlert as AlertTriangle, CircleCheck as CheckCircle2, Save, Loader as Loader2 } from 'lucide-react';

interface Product {
  id: string;
  name: string;
  btw_percentage: number;
}

interface Variant {
  id: string;
  product_id: string;
  size: string;
  type: string;
  purchase_price_cents: number | null;
  plants_per_box: number | null;
  boxes_per_pallet_position: number;
  box_cost_cents: number | null;
  price_rounding_increment_cents: number;
  transport_method: string;
  active: boolean;
  products: Product;
}

interface MarginTier {
  id: string;
  product_id: string;
  minimum_quantity: number;
  maximum_quantity: number | null;
  margin_percentage: number;
  sort_order: number;
  active: boolean;
}

export default function AdminPlantPricing() {
  const [activeTab, setActiveTab] = useState('calculator');

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <Breadcrumb pageName="Prijscalculator" />
      <div>
        <h1 className="text-4xl font-bold text-jv-primary mb-2">Prijscalculator</h1>
        <p className="text-gray-600 mt-1">Bereken verkoopprijzen op basis van inkoopprijzen, marges en logistieke kosten</p>
      </div>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="calculator" className="flex items-center gap-2">
            <Calculator className="w-4 h-4" />Calculator
          </TabsTrigger>
          <TabsTrigger value="producten" className="flex items-center gap-2">
            <Package className="w-4 h-4" />Producten
          </TabsTrigger>
          <TabsTrigger value="marges" className="flex items-center gap-2">
            <Layers className="w-4 h-4" />Marges
          </TabsTrigger>
        </TabsList>

        <TabsContent value="calculator" className="mt-6">
          <CalculatorTab />
        </TabsContent>
        <TabsContent value="producten" className="mt-6">
          <ProductenTab />
        </TabsContent>
        <TabsContent value="marges" className="mt-6">
          <MargesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// --- Calculator Tab ---

function CalculatorTab() {
  const [products, setProducts] = useState<Product[]>([]);
  const [variants, setVariants] = useState<Variant[]>([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [quantity, setQuantity] = useState(60);
  const { data, error, isLoading, calculate } = usePlantPriceCalculation({ debounceMs: 500 });

  useEffect(() => {
    loadProducts();
  }, []);

  async function loadProducts() {
    const { data } = await supabase
      .from('products')
      .select('id, name, btw_percentage')
      .eq('active', true)
      .eq('is_simple_product', false)
      .order('name');
    if (data) setProducts(data);
  }

  async function loadVariants(productId: string) {
    const { data } = await supabase
      .from('product_variants')
      .select('id, product_id, size, type, purchase_price_cents, plants_per_box, boxes_per_pallet_position, box_cost_cents, price_rounding_increment_cents, transport_method, active, products(id, name, btw_percentage)')
      .eq('product_id', productId)
      .eq('active', true)
      .order('size');
    if (data) setVariants(data as unknown as Variant[]);
  }

  function handleProductChange(productId: string) {
    setSelectedProductId(productId);
    setSelectedVariantId('');
    loadVariants(productId);
  }

  function handleCalculate() {
    if (!selectedVariantId || quantity < 1) return;
    calculate({
      variantId: selectedVariantId,
      quantity,
      previewOverrides: undefined,
    });
  }

  useEffect(() => {
    if (selectedVariantId && quantity > 0) {
      handleCalculate();
    }
  }, [selectedVariantId, quantity]);

  const selectedVariant = variants.find(v => v.id === selectedVariantId);
  const isConfigured = selectedVariant?.purchase_price_cents != null && selectedVariant?.plants_per_box != null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Invoer</CardTitle>
          <CardDescription>Selecteer product, variant en aantal</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Product</Label>
            <Select value={selectedProductId} onValueChange={handleProductChange}>
              <SelectTrigger><SelectValue placeholder="Kies een product..." /></SelectTrigger>
              <SelectContent>
                {products.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Variant</Label>
            <Select value={selectedVariantId} onValueChange={setSelectedVariantId} disabled={!selectedProductId}>
              <SelectTrigger><SelectValue placeholder="Kies een variant..." /></SelectTrigger>
              <SelectContent>
                {variants.map(v => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.size} ({v.type})
                    {v.purchase_price_cents == null && ' - Niet geconfigureerd'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Aantal planten</Label>
            <Input
              type="number"
              min={1}
              value={quantity}
              onChange={e => setQuantity(parseInt(e.target.value) || 0)}
            />
          </div>

          {selectedVariant && !isConfigured && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Deze variant mist configuratie. Stel de inkoopprijs en planten per doos in via het Producten-tabblad.
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            Resultaat
            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data ? (
            <div className="space-y-4">
              {data.requiresManualQuote && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Transportkosten vereisen handmatige offerte ({data.logistics.palletPositions} palletplaatsen)
                  </AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="font-medium text-gray-500">Verkoopprijs excl. BTW</div>
                <div className="font-bold text-lg text-green-700">{formatEuroCents(data.pricing.sellingPriceCents)}</div>

                <div className="font-medium text-gray-500">Verkoopprijs incl. BTW ({data.pricing.btwPercentage}%)</div>
                <div className="font-bold text-lg">{formatEuroCents(data.pricing.sellingPriceInclVatCents)}</div>
              </div>

              <Separator />

              <div className="text-sm font-semibold text-gray-700 mb-2">Opbouw per plant</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-gray-500">Inkoopprijs</div>
                <div>{formatEuroCents(data.pricing.purchasePriceCents)}</div>
                <div className="text-gray-500">Marge</div>
                <div>{formatEuroCents(data.pricing.marginPerPlantCents)}</div>
                <div className="text-gray-500">Logistiek</div>
                <div>{formatEuroCents(data.logistics.logisticsPerPlantCents)}</div>
                <div className="text-gray-500">Afronding naar</div>
                <div>{data.pricing.roundingIncrementCents} cent</div>
              </div>

              <Separator />

              <div className="text-sm font-semibold text-gray-700 mb-2">Logistiek</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-gray-500">Dozen benodigd</div>
                <div>{data.logistics.boxesRequired} ({data.logistics.plantsPerBox} planten/doos)</div>
                <div className="text-gray-500">Palletplaatsen</div>
                <div>{data.logistics.palletPositions}</div>
                <div className="text-gray-500">Dooskosten</div>
                <div>{formatEuroCents(data.logistics.totalBoxCostCents)}</div>
                <div className="text-gray-500">Transportkosten</div>
                <div>{formatEuroCents(data.logistics.transportCostCents)}</div>
                <div className="text-gray-500">Totaal logistiek</div>
                <div className="font-medium">{formatEuroCents(data.logistics.totalLogisticsCents)}</div>
              </div>

              <Separator />

              <div className="text-sm font-semibold text-gray-700 mb-2">Totalen ({data.input.quantity} planten)</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-gray-500">Omzet</div>
                <div>{formatEuroCents(data.totals.totalRevenueCents)}</div>
                <div className="text-gray-500">Inkoop</div>
                <div>{formatEuroCents(data.totals.totalPurchaseCents)}</div>
                <div className="text-gray-500">Logistiek</div>
                <div>{formatEuroCents(data.totals.totalLogisticsCents)}</div>
                <div className="text-gray-500">Winst</div>
                <div className={`font-bold ${data.totals.profitCents >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {formatEuroCents(data.totals.profitCents)} ({data.totals.profitMarginPercent}%)
                </div>
              </div>

              <Separator />

              <div className="text-sm font-semibold text-gray-700 mb-2">Gratis transport</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-gray-500">Orderwaarde incl. BTW</div>
                <div>{formatEuroCents(data.transportPolicy.orderValueInclVatCents)}</div>
                <div className="text-gray-500">Drempel</div>
                <div>{formatEuroCents(data.transportPolicy.freeTransportThresholdCents)}</div>
                <div className="text-gray-500">Gratis transport?</div>
                <div>
                  {data.transportPolicy.qualifiesForFreeTransport
                    ? <Badge className="bg-green-100 text-green-800">Ja</Badge>
                    : <Badge variant="outline">Nee</Badge>
                  }
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-400 text-sm">Selecteer een geconfigureerde variant en voer een aantal in om de prijs te berekenen.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// --- Producten Tab ---

function ProductenTab() {
  const [variants, setVariants] = useState<Variant[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, Partial<Variant>>>({});

  useEffect(() => { loadVariants(); }, []);

  async function loadVariants() {
    setLoading(true);
    const { data } = await supabase
      .from('product_variants')
      .select('id, product_id, size, type, purchase_price_cents, plants_per_box, boxes_per_pallet_position, box_cost_cents, price_rounding_increment_cents, transport_method, active, products(id, name, btw_percentage)')
      .eq('active', true)
      .order('size');
    if (data) setVariants(data as unknown as Variant[]);
    setLoading(false);
  }

  function getEditValue(variantId: string, field: keyof Variant): string {
    const edit = editValues[variantId];
    if (edit && field in edit) return String(edit[field] ?? '');
    const variant = variants.find(v => v.id === variantId);
    if (!variant) return '';
    const val = variant[field];
    return val == null ? '' : String(val);
  }

  function setEditField(variantId: string, field: keyof Variant, value: string) {
    setEditValues(prev => ({
      ...prev,
      [variantId]: { ...prev[variantId], [field]: value === '' ? null : parseInt(value) }
    }));
  }

  async function saveVariant(variantId: string) {
    const edit = editValues[variantId];
    if (!edit) return;
    setSaving(variantId);
    const update: Record<string, any> = {};
    if ('purchase_price_cents' in edit) update.purchase_price_cents = edit.purchase_price_cents;
    if ('plants_per_box' in edit) update.plants_per_box = edit.plants_per_box;
    if ('boxes_per_pallet_position' in edit) update.boxes_per_pallet_position = edit.boxes_per_pallet_position || 2;
    if ('box_cost_cents' in edit) update.box_cost_cents = edit.box_cost_cents;
    if ('price_rounding_increment_cents' in edit) update.price_rounding_increment_cents = edit.price_rounding_increment_cents || 5;

    await supabase.from('product_variants').update(update).eq('id', variantId);
    setEditValues(prev => { const n = { ...prev }; delete n[variantId]; return n; });
    await loadVariants();
    setSaving(null);
  }

  const hasChanges = (id: string) => !!editValues[id] && Object.keys(editValues[id]).length > 0;

  // Group variants by product
  const grouped = variants.reduce<Record<string, { product: Product; variants: Variant[] }>>((acc, v) => {
    const pid = v.product_id;
    if (!acc[pid]) acc[pid] = { product: v.products, variants: [] };
    acc[pid].variants.push(v);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Variant Configuratie</CardTitle>
          <CardDescription>Stel inkoopprijs, planten per doos en overige parameters in per variant</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-gray-400 py-8 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Laden...
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(grouped).map(([productId, { product, variants: pvs }]) => (
                <div key={productId}>
                  <h3 className="font-semibold text-gray-800 mb-2">{product.name}</h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[120px]">Maat</TableHead>
                          <TableHead className="w-[80px]">Type</TableHead>
                          <TableHead className="w-[120px]">Inkoop (ct)</TableHead>
                          <TableHead className="w-[100px]">Planten/doos</TableHead>
                          <TableHead className="w-[100px]">Dozen/pallet</TableHead>
                          <TableHead className="w-[120px]">Dooskost (ct)</TableHead>
                          <TableHead className="w-[100px]">Afronding (ct)</TableHead>
                          <TableHead className="w-[80px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pvs.map(v => (
                          <TableRow key={v.id} className={v.purchase_price_cents == null ? 'bg-[#f5eede]/50' : ''}>
                            <TableCell className="font-medium">{v.size}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">{v.type}</Badge>
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                className="w-24 h-8 text-sm"
                                value={getEditValue(v.id, 'purchase_price_cents')}
                                onChange={e => setEditField(v.id, 'purchase_price_cents', e.target.value)}
                                placeholder="--"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                className="w-20 h-8 text-sm"
                                value={getEditValue(v.id, 'plants_per_box')}
                                onChange={e => setEditField(v.id, 'plants_per_box', e.target.value)}
                                placeholder="--"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                className="w-20 h-8 text-sm"
                                value={getEditValue(v.id, 'boxes_per_pallet_position')}
                                onChange={e => setEditField(v.id, 'boxes_per_pallet_position', e.target.value)}
                                placeholder="2"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                className="w-24 h-8 text-sm"
                                value={getEditValue(v.id, 'box_cost_cents')}
                                onChange={e => setEditField(v.id, 'box_cost_cents', e.target.value)}
                                placeholder="Standaard"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                className="w-20 h-8 text-sm"
                                value={getEditValue(v.id, 'price_rounding_increment_cents')}
                                onChange={e => setEditField(v.id, 'price_rounding_increment_cents', e.target.value)}
                                placeholder="5"
                              />
                            </TableCell>
                            <TableCell>
                              {hasChanges(v.id) && (
                                <Button
                                  size="sm"
                                  className="h-8"
                                  onClick={() => saveVariant(v.id)}
                                  disabled={saving === v.id}
                                >
                                  {saving === v.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// --- Marges Tab ---

interface MargesVariant {
  id: string;
  size: string;
  type: string;
}

function MargesTab() {
  const [products, setProducts] = useState<Product[]>([]);
  const [variants, setVariants] = useState<MargesVariant[]>([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedVariantId, setSelectedVariantId] = useState<string>('__product__');
  const [tiers, setTiers] = useState<MarginTier[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTier, setEditTier] = useState<MarginTier | null>(null);
  const [form, setForm] = useState({ min: '', max: '', margin: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadProducts(); }, []);

  async function loadProducts() {
    const { data } = await supabase
      .from('products')
      .select('id, name, btw_percentage')
      .eq('active', true)
      .eq('is_simple_product', false)
      .order('name');
    if (data) setProducts(data);
  }

  async function loadVariants(productId: string) {
    const { data } = await supabase
      .from('product_variants')
      .select('id, size, type')
      .eq('product_id', productId)
      .eq('active', true)
      .order('size');
    if (data) setVariants(data);
  }

  async function loadTiers(productId: string, variantId: string) {
    setLoading(true);
    let query = supabase
      .from('plant_margin_tiers')
      .select('*')
      .eq('product_id', productId);

    if (variantId === '__product__') {
      query = query.is('variant_id', null);
    } else {
      query = query.eq('variant_id', variantId);
    }

    const { data } = await query.order('sort_order');
    if (data) setTiers(data);
    setLoading(false);
  }

  function handleProductChange(productId: string) {
    setSelectedProductId(productId);
    setSelectedVariantId('__product__');
    setTiers([]);
    loadVariants(productId);
    loadTiers(productId, '__product__');
  }

  function handleVariantChange(variantId: string) {
    setSelectedVariantId(variantId);
    if (selectedProductId) {
      loadTiers(selectedProductId, variantId);
    }
  }

  function openAddDialog() {
    setEditTier(null);
    setForm({ min: '', max: '', margin: '' });
    setDialogOpen(true);
  }

  function openEditDialog(tier: MarginTier) {
    setEditTier(tier);
    setForm({
      min: String(tier.minimum_quantity),
      max: tier.maximum_quantity != null ? String(tier.maximum_quantity) : '',
      margin: String(tier.margin_percentage),
    });
    setDialogOpen(true);
  }

  async function saveTier() {
    if (!selectedProductId || !form.min || !form.margin) return;
    setSaving(true);

    const marginPct = parseFloat(form.margin);
    if (isNaN(marginPct) || marginPct < 0 || marginPct > 100) {
      setSaving(false);
      return;
    }

    const payload: Record<string, any> = {
      product_id: selectedProductId,
      variant_id: selectedVariantId === '__product__' ? null : selectedVariantId,
      minimum_quantity: parseInt(form.min),
      maximum_quantity: form.max ? parseInt(form.max) : null,
      margin_percentage: marginPct,
      sort_order: editTier ? editTier.sort_order : tiers.length + 1,
      active: true,
    };

    if (editTier) {
      await supabase.from('plant_margin_tiers').update(payload).eq('id', editTier.id);
    } else {
      await supabase.from('plant_margin_tiers').insert(payload);
    }

    setSaving(false);
    setDialogOpen(false);
    loadTiers(selectedProductId, selectedVariantId);
  }

  async function deleteTier(id: string) {
    await supabase.from('plant_margin_tiers').delete().eq('id', id);
    loadTiers(selectedProductId, selectedVariantId);
  }

  const isVariantLevel = selectedVariantId !== '__product__';

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Margetabellen</CardTitle>
          <CardDescription>Configureer margetiers per product of per variant. De marge is een percentage dat wordt toegepast op de inkoopprijs plus logistieke kosten per plant, op basis van het bestelde aantal.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px] max-w-sm space-y-2">
              <Label>Product</Label>
              <Select value={selectedProductId} onValueChange={handleProductChange}>
                <SelectTrigger><SelectValue placeholder="Kies een product..." /></SelectTrigger>
                <SelectContent>
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedProductId && (
              <div className="flex-1 min-w-[200px] max-w-sm space-y-2">
                <Label>Niveau</Label>
                <Select value={selectedVariantId} onValueChange={handleVariantChange}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__product__">Product-niveau (alle varianten)</SelectItem>
                    {variants.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.size} ({v.type})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {selectedProductId && (
              <Button onClick={openAddDialog} className="flex items-center gap-2">
                <Plus className="w-4 h-4" /> Tier toevoegen
              </Button>
            )}
          </div>

          {isVariantLevel && (
            <Alert className="border-[#e7e1d6] bg-[#fcfaf6]">
              <AlertDescription className="text-[#5a6552] text-sm">
                Variant-specifieke marges overschrijven de product-niveau marges. Alleen deze marges worden gebruikt voor de geselecteerde variant.
              </AlertDescription>
            </Alert>
          )}

          {selectedProductId && (
            loading ? (
              <div className="flex items-center gap-2 text-gray-400 py-8 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Laden...
              </div>
            ) : tiers.length === 0 ? (
              <p className="text-gray-400 text-sm py-4">
                {isVariantLevel
                  ? 'Geen variant-specifieke marges. De product-niveau of standaard marges worden gebruikt.'
                  : 'Geen margetiers geconfigureerd voor dit product.'}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Van</TableHead>
                    <TableHead>Tot</TableHead>
                    <TableHead>Marge (%)</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tiers.map(tier => (
                    <TableRow key={tier.id}>
                      <TableCell>{tier.minimum_quantity} stuks</TableCell>
                      <TableCell>{tier.maximum_quantity != null ? `${tier.maximum_quantity} stuks` : 'Onbeperkt'}</TableCell>
                      <TableCell className="font-medium">{Number(tier.margin_percentage).toFixed(2)}%</TableCell>
                      <TableCell>
                        {tier.active
                          ? <Badge className="bg-green-100 text-green-800">Actief</Badge>
                          : <Badge variant="outline">Inactief</Badge>
                        }
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(tier)}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => deleteTier(tier.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{editTier ? 'Margetier bewerken' : 'Nieuwe margetier'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Minimum aantal</Label>
                <Input
                  type="number"
                  min={1}
                  value={form.min}
                  onChange={e => setForm(f => ({ ...f, min: e.target.value }))}
                  placeholder="1"
                />
              </div>
              <div className="space-y-2">
                <Label>Maximum aantal (leeg = onbeperkt)</Label>
                <Input
                  type="number"
                  min={1}
                  value={form.max}
                  onChange={e => setForm(f => ({ ...f, max: e.target.value }))}
                  placeholder="Onbeperkt"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Marge (%)</Label>
              <Input
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={form.margin}
                onChange={e => setForm(f => ({ ...f, margin: e.target.value }))}
                placeholder="Bijv. 25 voor 25%"
              />
              <p className="text-xs text-gray-500">Toegepast op inkoopprijs + logistieke kosten per plant.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuleren</Button>
            <Button onClick={saveTier} disabled={saving || !form.min || !form.margin}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
